import { estimatedColumnWidth } from "../helpers";
import { formatCurrency, formatDate } from "@/utils/helpers";

export function getAgingColumns() {
  const colArr = [
    {
      label: "Type",
      value: true,
      columnId: "type",
      width: estimatedColumnWidth("type"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Not Due",
      value: true,
      columnId: "notDue",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "1 Month Overdue",
      value: true,
      columnId: "oneMonth",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "2 Months Overdue",
      value: true,
      columnId: "twoMonth",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "3 Months Overdue",
      value: true,
      columnId: "threeMonth",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "Over 3 Months Overdue",
      value: true,
      columnId: "overThreeMonths",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "Total",
      value: true,
      columnId: "total",
      width: estimatedColumnWidth("amount"),
      resizable: true,
      formatter: (value) => formatCurrency(String(value || ""))
    }

  ];

  return colArr;
}

// What settled an invoice is split three ways rather than lumped into one Paid figure, because the three are
// not the same kind of thing: cash paid, an advance given earlier and adjusted against the invoice, and value
// credited by a credit note. All three reduce the Balance (Balance = Total - Paid - Advance - Credit Note, the
// same figure the Aging report arrives at), and each is followed by the documents behind it, because "which
// payment cleared this?" is the first question asked of any of them. A line settled by several documents lists
// them all in one cell.
//
// Money columns marked `summable` are the ones the group and grand-total footers add up.
const formatMoneyCell = (value) => String(formatCurrency(value || 0) || "");

export function getSupplierStatementColumns() {
  const colArr = [
    {
      label: "Type",
      value: true,
      columnId: "type",
      width: estimatedColumnWidth("type"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Number",
      value: true,
      columnId: "number",
      width: estimatedColumnWidth("number"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Creation Date",
      value: true,
      columnId: "creationDate",
      width: estimatedColumnWidth("date"),
      resizable: true,
      formatter: (value) => (value ? formatDate(new Date(value)) : "")
    },
    {
      label: "Invoice Num.", value: true, columnId: "supplierInvoiceNr", width: estimatedColumnWidth("number"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Applied Against", value: true, columnId: "appliedInvoiceNumber", width: estimatedColumnWidth("number"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Due Date", value: true, columnId: "dueDate", width: estimatedColumnWidth("date"), resizable: true, formatter: (value) => (value ? formatDate(new Date(value)) : "")
    },
    {
      label: "Transaction Date", value: true, columnId: "transactionDate", width: estimatedColumnWidth("date"), resizable: true, formatter: (value) => (value ? formatDate(new Date(value)) : "")
    },
    // What the invoice itself says (Approved, Partial, Paid, Cancelled), and where the figures put it. They normally
    // agree; a "Paid" invoice that still has a balance is exactly what the reader needs to notice.
    {
      label: "Status", value: true, columnId: "invoiceStatus", width: 100, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Settlement", value: true, columnId: "settlementStatus", width: 120, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Amount", value: true, summable: true, columnId: "amount", width: 115, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "VAT", value: true, summable: true, columnId: "vat", width: 90, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "Total", value: true, summable: true, columnId: "total", width: 120, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "Paid", value: true, summable: true, columnId: "paidAmount", width: 115, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "Payment No", value: true, columnId: "paymentNumbers", width: 140, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Advance Adjusted", value: true, summable: true, columnId: "advanceAmount", width: 130, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "Advance Payment No", value: true, columnId: "advanceNumbers", width: 150, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Credit Note", value: true, summable: true, columnId: "creditNoteAmount", width: 115, resizable: true, formatter: formatMoneyCell
    },
    {
      label: "Credit Note No", value: true, columnId: "creditNoteNumbers", width: 140, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Balance", value: true, summable: true, columnId: "balance", width: 125, resizable: true, formatter: formatMoneyCell
    }
  ];

  return colArr;
}

// The short list of what is still open: the same document columns, without the VAT split and the settlement
// document numbers, which belong to the full list of invoices.
const OPEN_ITEM_COLUMN_IDS = [
  "type", "number", "supplierInvoiceNr", "appliedInvoiceNumber", "dueDate", "transactionDate", "settlementStatus",
  "total", "paidAmount", "advanceAmount", "creditNoteAmount", "balance"
];

export function getSupplierOpenItemColumns() {
  const all = getSupplierStatementColumns();

  return OPEN_ITEM_COLUMN_IDS.map((columnId) => all.find((column) => column.columnId === columnId));
}

// The supplier's statement of account: what we owe, movement by movement. Credit is a bill raised by the supplier
// and Debit a payment or credit note that reduces it, so a positive balance is an amount owed to the supplier.
// The last four columns must stay Debit / Credit / Balance / Running Balance: the totals footer in
// getReceiptRows pads by columns.length - (totals.length + 1) and fills the tail.
export function getSupplierLedgerColumns() {
  const formatDateCell = (value) => (value ? formatDate(new Date(value)) : "");
  const formatSigned = (value) => (value === null || value === undefined || value === "" ? "" : formatCurrency(String(value)));

  return [
    {
      label: "Date", value: true, columnId: "date", width: 95, resizable: true, formatter: formatDateCell
    },
    {
      label: "Document Number", value: true, columnId: "referenceNumber", width: 150, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Type", value: true, columnId: "type", width: 140, resizable: true, formatter: (value) => String(value || "").trim()
    },
    {
      label: "Description", value: true, columnId: "description", width: 260, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Debit", value: true, columnId: "debit", width: 130, resizable: true, formatter: (value) => (value ? formatCurrency(String(value || "")) : "")
    },
    {
      label: "Credit", value: true, columnId: "credit", width: 130, resizable: true, formatter: (value) => (value ? formatCurrency(String(value || "")) : "")
    },
    {
      label: "Balance", value: true, columnId: "balance", width: 130, resizable: true, formatter: formatSigned
    },
    {
      label: "Running Balance", value: true, columnId: "runningBalance", width: 150, resizable: true, formatter: formatSigned
    },
  ];
}

export function getAdvancePaymentColumns() {
  const colArr = [
    {
      label: "Advance Payment Number",
      value: true,
      columnId: "number",
      width: estimatedColumnWidth("number"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Payment Date",
      value: true,
      columnId: "date",
      width: estimatedColumnWidth("date"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Payment Type", value: true, columnId: "type", width: estimatedColumnWidth("type"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Amount", value: true, columnId: "amount", width: estimatedColumnWidth("amount"), resizable: true, formatter: (value) => String(formatCurrency(value || 0) || "")
    }
  ];

  return colArr;
}

export function getSupplierPaidInvoicesColumns() {
  const colArr = [
    {
      label: "Type",
      value: true,
      columnId: "invoiceType",
      width: estimatedColumnWidth("type"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Invoice Number",
      value: true,
      columnId: "invoiceNumber",
      width: estimatedColumnWidth("number"),
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Expense Number", value: true, columnId: "expenseNumber", width: estimatedColumnWidth("number"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Due Date", value: true, columnId: "dueDate", width: estimatedColumnWidth("date"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Actual Date", value: true, columnId: "actualDate", width: estimatedColumnWidth("date"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Payment Method", value: true, columnId: "paymentMethod", width: estimatedColumnWidth("method"), resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Amount", value: true, columnId: "amount", width: estimatedColumnWidth("amount"), resizable: true, formatter: (value) => formatCurrency(String(value || ""))
    },
    {
      label: "Paid", value: true, columnId: "paid", width: estimatedColumnWidth("amount"), resizable: true, formatter: (value) => formatCurrency(String(value || ""))
    }
  ];

  return colArr;
}
